import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authAPI, changePassword as changePasswordAPI } from "../services/api";
import { api } from "../services/apiService";
import {
  storeAuthData,
  getStoredToken,
  getStoredUser,
  clearAuthData,
  isTokenExpired,
} from "../utils/tokenUtils";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Function to refresh user data from API
  const refreshUser = useCallback(async () => {
    try {
      console.log("AuthContext: Refreshing user data...");
      const response = await api.getUserProfile();
      console.log("AuthContext: getUserProfile response:", response);

      // api.getUserProfile returns response.data which is the JSON response
      // Backend returns: { success: true, message: "...", data: {...} }
      const payload = response;
      const userData =
        payload?.success === true && payload?.data
          ? payload.data
          : payload?.user_id
            ? payload
            : null;

      console.log("AuthContext: Extracted userData:", userData);
      console.log("AuthContext: userData.client_id:", userData?.client_id);
      console.log(
        "AuthContext: userData.profile_image:",
        userData?.profile_image,
      );

      if (userData && userData.user_id) {
        // Update stored user data
        const token = getStoredToken();
        const expiresAt =
          localStorage.getItem("tokenExpiresAt") ||
          localStorage.getItem("auth_expires_at");
        if (token) {
          storeAuthData(token, userData, expiresAt);
          console.log("AuthContext: Stored updated user data in localStorage");
        }
        setUser(userData);
        console.log(
          "AuthContext: User state updated with client_id:",
          userData.client_id,
        );
        return userData;
      } else {
        console.warn("AuthContext: No valid userData found in response");
      }
    } catch (error) {
      console.error("AuthContext: Error refreshing user data:", error);
    }
    return null;
  }, []);

  useEffect(() => {
    // Check if user is already logged in on app start
    const initializeAuth = async () => {
      const token = getStoredToken();
      const savedUser = getStoredUser();

      if (token && savedUser && !isTokenExpired(token)) {
        // Set initial user from localStorage for immediate display
        setUser(savedUser);
        setIsAuthenticated(true);

        // Then refresh user data from API to get latest profile_image and other updates
        try {
          console.log("AuthContext: Refreshing user data from API on mount...");
          const refreshedUser = await refreshUser();
          if (refreshedUser) {
            console.log(
              "AuthContext: User data refreshed successfully on mount",
            );
          } else {
            console.warn(
              "AuthContext: Failed to refresh user data, using cached data",
            );
          }
        } catch (error) {
          console.error(
            "AuthContext: Error refreshing user data on mount:",
            error,
          );
          // Continue with cached user data if refresh fails
        }
      } else {
        // Token is expired or invalid, clear everything
        clearAuthData();
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };

    initializeAuth();

    // Listen for profile update events to refresh user data
    const handleProfileUpdated = () => {
      if (isAuthenticated) {
        console.log("Profile updated event received, refreshing user data...");
        refreshUser();
      }
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdated);
    };
  }, [isAuthenticated, refreshUser]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const data = response?.data ?? response;
      if (data?.auth_token && data?.user) {
        storeAuthData(data.auth_token, data.user, data.expires_at);
        setUser(data.user);
        setIsAuthenticated(true);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const setAuthFromLoginResult = (authToken, userData, expiresAt) => {
    if (authToken && userData) {
      storeAuthData(authToken, userData, expiresAt);
      setUser(userData);
      setIsAuthenticated(true);
    }
  };

  const verifyOTP = async (email, otp) => {
    try {
      const response = await authAPI.verifyOTP(email, otp);
      // When client has MFA enabled, backend returns requires_mfa + mfa_temp_token instead of auth_token
      if (response?.requires_mfa && response?.mfa_temp_token) {
        return {
          ...response,
          requires_mfa: true,
          mfa_temp_token: response.mfa_temp_token,
        };
      }
      const authToken = response?.auth_token || response?.data?.auth_token;
      const userData = response?.user || response?.data?.user;
      const expiresAt = response?.expires_at || response?.data?.expires_at;
      const redirectPath =
        response?.redirect_path || response?.data?.redirect_path;
      if (authToken && userData) {
        storeAuthData(authToken, userData, expiresAt);
        setUser(userData);
        setIsAuthenticated(true);
        return {
          ...response,
          user: userData,
          redirectPath,
          data: {
            ...(response.data || response),
            user: userData,
            redirect_path: redirectPath,
            requires_password_change:
              response?.requires_password_change ||
              response?.data?.requires_password_change,
          },
        };
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const verifyMfa = async (mfaTempToken, code) => {
    try {
      const response = await authAPI.verifyMfa(mfaTempToken, code);
      const authToken = response?.auth_token || response?.data?.auth_token;
      const userData = response?.user || response?.data?.user;
      const expiresAt = response?.expires_at || response?.data?.expires_at;
      const redirectPath =
        response?.redirect_path || response?.data?.redirect_path;
      if (authToken && userData) {
        storeAuthData(authToken, userData, expiresAt);
        setUser(userData);
        setIsAuthenticated(true);
        return {
          ...response,
          user: userData,
          redirectPath,
          data: {
            ...(response.data || response),
            user: userData,
            redirect_path: redirectPath,
            requires_password_change:
              response?.requires_password_change ||
              response?.data?.requires_password_change,
          },
        };
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (
    currentPassword,
    newPassword,
    isFirstTime = false,
  ) => {
    try {
      const response = await changePasswordAPI(
        currentPassword,
        newPassword,
        isFirstTime,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call backend logout API to invalidate token
      const token = getStoredToken();
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error("Logout API error:", error);
      // Continue with logout even if API call fails
    } finally {
      // Clear all auth data using utility function
      clearAuthData();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const signup = async (signupData) => {
    try {
      const response = await authAPI.signup(signupData);
      if (response?.success && response?.data?.token) {
        const { user_id, email, full_name, client_id, token } = response.data;
        const userData = {
          user_id,
          email,
          full_name,
          client_id,
          role_name: "admin",
        };
        storeAuthData(token, userData);
        setUser(userData);
        setIsAuthenticated(true);
        return response.data;
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = async (token, userData) => {
    try {
      // Store token and user data
      if (token && userData) {
        storeAuthData(token, userData);
        setUser(userData);
        setIsAuthenticated(true);

        // Refresh user data from API to ensure we have the latest client_id and other fields
        try {
          const refreshedUser = await refreshUser();
          if (refreshedUser) {
            // Update with refreshed data
            setUser(refreshedUser);
          }
        } catch (refreshError) {
          console.error(
            "Error refreshing user after Google login:",
            refreshError,
          );
          // Continue with the userData from OAuth callback if refresh fails
        }

        return { user: userData, token };
      }
      throw new Error("Invalid token or user data");
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verifyOTP,
    verifyMfa,
    setAuthFromLoginResult,
    logout,
    register,
    signup,
    changePassword,
    refreshUser,
    loginWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
